/**
 * SetupPage - Wizard de registro inicial para nuevos usuarios
 * Paso 1: Buscar club (código de invitación o búsqueda por nombre)
 * Paso 2: Datos personales + contacto de emergencia
 * Paso 3: Revisión y envío de solicitud
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { COUNTRY_CODES } from "@/lib/countryCodes";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Search,
  Hash,
  ChevronRight,
  ChevronLeft,
  Loader2,
  CheckCircle,
  AlertCircle,
  AlertTriangle,
  Info,
  User,
  Heart,
  Send,
  LogOut,
  Plus,
  Shield,
  Users,
  X,
  Camera,
  CreditCard,
  FileText,
} from "lucide-react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Input,
  Select,
  DocumentUpload,
} from "@/components/ui";
import { Modal } from "@/components/ui/Modal";
import EpsSelect from "@/components/forms/EpsSelect";
import { DatePickerField } from "@/components/ui/DatePicker";
import { useAuthStore } from "@/stores/authStore";
import {
  clubService,
  associationService,
  churchService,
  districtService,
  generateInviteCode,
} from "@/lib/services/organization";
import { joinRequestService } from "@/lib/services/joinRequests";
import { personService } from "@/lib/services/persons";
import { createClubApprovalRequest } from "@/lib/services/clubApprovalRequests";
import {
  uploadJoinRequestPhoto,
  uploadJoinRequestDocumentFront,
  uploadJoinRequestDocumentBack,
  uploadJoinRequestEps,
} from "@/lib/services/storage";
import type { EncryptionMeta } from "@/lib/services/crypto";
import {
  getClubTypeByAge,
  deriveSectionFromBirthDate,
  getActiveSections,
} from "@/lib/utils";
import { CLUB_TYPE_LABELS } from "@/types";
import { EMERGENCY_CONTACT_RELATIONSHIPS } from "@/lib/constants/relationships";
import type {
  Club,
  ClubType,
  SectionConfig,
  Association,
  Church,
  District,
} from "@/types";

// ============================================
// SCHEMA
// ============================================

const personalSchema = z.object({
  firstName: z.string().min(2, "Mínimo 2 caracteres"),
  lastName: z.string().min(2, "Mínimo 2 caracteres"),
  // memberType se deriva de birthDate, no se selecciona
  idDocumentType: z.enum(["CC", "TI", "RC", "CE", "PASAPORTE", "PPT"]),
  idDocumentNumber: z.string().min(5, "Mínimo 5 caracteres"),
  bloodType: z.enum(["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"]),
  birthDate: z
    .string()
    .min(1, "Requerida")
    .refine(
      (d) => !d || new Date(d) <= new Date(),
      "La fecha no puede ser futura",
    )
    .refine(
      (d) => !d || new Date(d) >= new Date("1900-01-01"),
      "Fecha inválida",
    ),
  gender: z.enum(["male", "female"]),
  phoneCountryCode: z.string().default("+57"),
  phone: z.string().min(7, "Teléfono requerido"),
  healthInsurance: z.string().optional(),
  email: z.string().email("Email inválido").optional().or(z.literal("")),
  address: z.string().min(1, "Dirección requerida"),
  emergencyContactName: z.string().min(1, "Requerido"),
  emergencyContactRelationship: z.string().min(1, "Requerida"),
  emergencyContactCountryCode: z.string().default("+57"),
  emergencyContactPhone: z.string().min(7, "Requerido"),
});

type PersonalForm = z.infer<typeof personalSchema>;

// ============================================
// CONSTANTS
// ============================================

const documentTypeLabels: Record<string, string> = {
  CC: "Cédula de Ciudadanía",
  TI: "Tarjeta de Identidad",
  RC: "Registro Civil",
  CE: "Cédula de Extranjería",
  PASAPORTE: "Pasaporte",
  PPT: "Permiso por Protección Temporal (PPT)",
};

const bloodTypes = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];

const relationships = [...EMERGENCY_CONTACT_RELATIONSHIPS];

// ============================================
// NORMALIZERS
// ============================================

/** Capitaliza la primera letra de cada palabra */
const toTitleCase = (s: string) =>
  s.trim().replace(/(^|\s)\S/g, (c) => c.toUpperCase());

/** Bloquea teclas no numéricas; úsalo en onKeyDown de campos numéricos */
const filterDigits = (e: {
  key: string;
  ctrlKey: boolean;
  metaKey: boolean;
  preventDefault: () => void;
}) => {
  if (e.ctrlKey || e.metaKey) return; // permite Ctrl+C / Cmd+V etc.
  if (/^(Backspace|Delete|Tab|Escape|Enter|Arrow|Home|End)/.test(e.key)) return;
  if (/^\d$/.test(e.key)) return;
  e.preventDefault();
};

const STEPS = [
  { title: "Buscar Club", icon: Search },
  { title: "Datos Personales", icon: User },
  { title: "Confirmar", icon: CheckCircle },
];

// ============================================
// TYPES
// ============================================

interface NewClubData {
  name: string;
  churchName: string;
  churchId: string;
  associationId: string;
  sections: {
    aventureros: boolean;
    conquistadores: boolean;
    guias_mayores: boolean;
  };
}

// ============================================
// COMPONENT
// ============================================

export default function SetupPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Leer invite de la URL; si no viene en URL, usar sessionStorage como fallback
  // (sobrevive a login/register/verify-email)
  // El código guardado CADUCA. Antes vivía para siempre en localStorage: quien
  // recibió un enlace de invitación hace meses entraba al wizard y se le
  // preseleccionaba aquel club, saltándose el paso donde se elige "buscar otro
  // club" o "soy director/a". Con las fusiones y renombres eso dejó gente
  // metiéndose a un club que ya no era el suyo (2026-08-06).
  const inviteGuardado = (() => {
    const enSesion = sessionStorage.getItem("pending_invite");
    if (enSesion) return enSesion; // la sesión actual: siempre vale
    const code = localStorage.getItem("pending_invite");
    if (!code) return "";
    const ts = Number(localStorage.getItem("pending_invite_at") ?? 0);
    const DIAS = 14;
    if (!ts || Date.now() - ts > DIAS * 864e5) {
      localStorage.removeItem("pending_invite");
      localStorage.removeItem("pending_invite_at");
      return "";
    }
    return code;
  })();
  const inviteFromUrl = searchParams.get("invite") || inviteGuardado || "";
  const { user, profile, signOut } = useAuthStore();
  // Si llega con invite por URL arrancamos en step 1 (se auto-resuelve el club)
  const [step, setStep] = useState(inviteFromUrl ? 1 : 0);
  const [selectedClub, setSelectedClub] = useState<Club | null>(null);
  const [isCreatingClub, setIsCreatingClub] = useState(false);

  /** Suelta la invitación guardada y devuelve a la persona a elegir camino. */
  const limpiarInvitacion = () => {
    sessionStorage.removeItem("pending_invite");
    localStorage.removeItem("pending_invite");
    localStorage.removeItem("pending_invite_at");
    setSelectedClub(null);
    setIsCreatingClub(false);
    setStep(0);
    // Quitar ?invite= de la URL para que un refresco no lo vuelva a tomar.
    if (searchParams.get("invite")) {
      window.location.replace("/setup");
    }
  };
  const [newClubData, setNewClubData] = useState<NewClubData | null>(null);
  // Camino "mi club YA está registrado y vengo a dirigir una sección".
  // Es el caso del director de Aventureros de un club que hasta ahora solo
  // tenía Conquistadores: no debe crear un club nuevo (sería duplicado), se
  // une al existente declarando qué sección dirige. Al aprobar, la Cloud
  // Function le asigna esa dirección y activa la sección si hacía falta.
  const [asSectionDirector, setAsSectionDirector] = useState(false);
  const [requestedSection, setRequestedSection] = useState<ClubType | null>(
    null,
  );
  // Confirmación explícita del usuario "vengo como miembro" para bloquear el
  // avance silencioso al flujo de miembro cuando en realidad es director/a de
  // sección (caso reportado 2026-08-03: usuaria terminó como miembro
  // fantasma sin joinRequest porque no vio la opción "Soy Director").
  const [roleConfirmedAsMember, setRoleConfirmedAsMember] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [inviteError, setInviteError] = useState<string | null>(null);

  // Documentos subidos con la solicitud (foto obligatoria + doc anverso/reverso).
  // Como el Person aún no existe, se suben a joinRequests/{userId}/ y los
  // URL/path se envían con la solicitud; la Cloud Function los copia al
  // Person al aprobar.
  const [uploadedPhoto, setUploadedPhoto] = useState<{
    url: string;
    path: string;
  } | null>(null);
  const [uploadedDocFront, setUploadedDocFront] = useState<{
    url: string;
    path: string;
    encryption: EncryptionMeta;
    contentType: string;
  } | null>(null);
  const [uploadedDocBack, setUploadedDocBack] = useState<{
    url: string;
    path: string;
    encryption: EncryptionMeta;
    contentType: string;
  } | null>(null);
  const [uploadedEps, setUploadedEps] = useState<{
    url: string;
    path: string;
    encryption: EncryptionMeta;
    contentType: string;
  } | null>(null);

  // Resolver club desde el param ?invite= al montar
  useEffect(() => {
    if (!inviteFromUrl || inviteFromUrl.length < 6) return;
    clubService
      .getByInviteCode(inviteFromUrl.toUpperCase())
      .then((club) => {
        if (club) {
          setSelectedClub(club);
        } else {
          setInviteError(
            "No se encontró un club con ese enlace. Verifica con tu director.",
          );
          setStep(0); // volver al buscador si falla
        }
      })
      .catch(() => {
        setInviteError("Error al procesar el enlace. Intenta de nuevo.");
        setStep(0);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const form = useForm<PersonalForm>({
    resolver: zodResolver(personalSchema),
    mode: "onTouched",
    defaultValues: {
      phoneCountryCode: "+57",
      emergencyContactCountryCode: "+57",
      email: user?.email || "",
    },
  });

  // Pre-fill del nombre.
  //
  // Manda la FICHA vinculada a esta cuenta, no el `displayName`. El nombre de
  // la cuenta se hereda de la primera solicitud que se hizo desde ella, y
  // cuando un padre inscribe a un hijo con su propio correo queda con el
  // nombre del hijo: en producción hay 79 cuentas así. A una directora le
  // apareció el nombre de su hija al ir a registrarse ella (2026-08-06).
  useEffect(() => {
    let cancelled = false;
    const partir = (nombre: string) => {
      const parts = nombre.trim().split(/\s+/);
      if (parts.length >= 4) {
        // Ej: "Diego Alejandro Lopez Camacho" → nombre: "Diego Alejandro", apellido: "Lopez Camacho"
        const mid = Math.floor(parts.length / 2);
        form.setValue("firstName", parts.slice(0, mid).join(" "));
        form.setValue("lastName", parts.slice(mid).join(" "));
      } else if (parts.length >= 2) {
        form.setValue("firstName", parts[0]);
        form.setValue("lastName", parts.slice(1).join(" "));
      } else if (parts[0]) {
        form.setValue("firstName", parts[0]);
      }
    };

    (async () => {
      if (profile?.personId) {
        try {
          const ficha = await personService.getById(profile.personId);
          // Sólo si la ficha es REALMENTE de esta cuenta.
          if (!cancelled && ficha?.linkedUserId === profile.id && ficha.fullName) {
            partir(ficha.fullName);
            return;
          }
        } catch {
          /* si no se puede leer, se cae al displayName */
        }
      }
      if (!cancelled && profile?.displayName) partir(profile.displayName);
    })();
    return () => {
      cancelled = true;
    };
  }, [profile, form]);

  const formValues = form.watch();

  // Calcular edad
  const age = formValues.birthDate
    ? Math.floor(
        (Date.now() - new Date(formValues.birthDate + "T12:00:00").getTime()) /
          (365.25 * 24 * 60 * 60 * 1000),
      )
    : null;

  const allowedClubType = age !== null ? getClubTypeByAge(age) : null;

  // Derivar memberType y section de la edad
  const derivedMemberType =
    age !== null ? (age >= 16 ? "lider" : "acampante") : "acampante";
  const derivedSection = formValues.birthDate
    ? deriveSectionFromBirthDate(new Date(formValues.birthDate + "T12:00:00"))
    : null;

  // Validar derivedSection contra secciones activas del club seleccionado.
  // Si la sección derivada no está activa, usar la sección más senior disponible.
  const SECTION_PRIORITY: ClubType[] = [
    "guias_mayores",
    "conquistadores",
    "aventureros",
  ];
  const effectiveSection = (() => {
    if (!derivedSection || !selectedClub) return derivedSection;
    const sections = selectedClub.sections;
    if (!sections) return derivedSection;
    if (sections[derivedSection as ClubType]?.active) return derivedSection;
    // La sección ideal no está activa → caer al more senior disponible
    return SECTION_PRIORITY.find((s) => sections[s]?.active) ?? derivedSection;
  })();
  const sectionAdjusted =
    !!derivedSection &&
    !!effectiveSection &&
    effectiveSection !== derivedSection;

  const handleNext = async () => {
    if (step === 0 && !selectedClub && !isCreatingClub) return;
    if (step === 0 && isCreatingClub && (!newClubData || !newClubData.name))
      return;
    if (step === 0 && asSectionDirector && !requestedSection) return;
    if (step === 1) {
      const valid = await form.trigger();
      if (!valid) {
        setTimeout(() => {
          const firstInvalid = document.querySelector<HTMLElement>(
            'input[aria-invalid="true"], select[aria-invalid="true"]',
          );
          firstInvalid?.closest(".space-y-6 > *, .grid > *")?.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }, 50);
        return;
      }
      // Documentos obligatorios: foto tipo carnet + doc anverso + doc reverso.
      const missing: string[] = [];
      if (!uploadedPhoto) missing.push("foto tipo carnet");
      if (!uploadedDocFront) missing.push("documento (anverso)");
      if (!uploadedDocBack) missing.push("documento (reverso)");
      if (missing.length > 0) {
        setSubmitError(
          `Faltan documentos obligatorios: ${missing.join(", ")}. Súbelos antes de continuar.`,
        );
        setTimeout(() => {
          document
            .getElementById("join-request-documents")
            ?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 50);
        return;
      }
      setSubmitError(null);
    }
    setStep((s) => Math.min(s + 1, 2));
  };

  const handleBack = () => setStep((s) => Math.max(s - 1, 0));

  const handleSubmit = async () => {
    if (!user || !profile) return;

    setSubmitting(true);
    setSubmitError(null);

    try {
      const data = form.getValues();

      if (isCreatingClub && newClubData) {
        // ========== FLUJO: CREAR CLUB (pendiente aprobación de asociación) ==========
        const sections: Record<string, SectionConfig> = {};
        if (newClubData.sections.aventureros) {
          sections.aventureros = { active: true };
        }
        if (newClubData.sections.conquistadores) {
          sections.conquistadores = { active: true };
        }
        if (newClubData.sections.guias_mayores) {
          sections.guias_mayores = { active: true };
        }

        // Crear el club en estado pendiente de aprobación
        const inviteCode = generateInviteCode();
        const clubId = await clubService.create(
          {
            name: newClubData.name,
            ...(newClubData.churchName
              ? { churchName: newClubData.churchName }
              : {}),
            ...(newClubData.churchId ? { churchId: newClubData.churchId } : {}),
            associationId: newClubData.associationId || "",
            inviteCode,
            sections: sections as Record<ClubType, SectionConfig>,
            type: Object.keys(sections)[0] as ClubType, // legacy: primera sección activa
            status: "inactive", // Pendiente — se activa cuando la asociación aprueba
            activeCount: 0,
            unitCount: 0,
          },
          profile.id,
        );

        // Crear solicitud de aprobación para la asociación
        await createClubApprovalRequest({
          userId: profile.id,
          userEmail: user.email || "",
          userName: `${data.firstName} ${data.lastName}`,
          clubId,
          clubName: newClubData.name,
          associationId: newClubData.associationId || "",
          personalData: {
            firstName: data.firstName,
            lastName: data.lastName,
            phone: `${data.phoneCountryCode}${data.phone}`,
            birthDate: new Date(data.birthDate + "T12:00:00"),
            idDocumentType: data.idDocumentType,
            idDocumentNumber: data.idDocumentNumber,
            gender: data.gender,
            bloodType: data.bloodType,
            address: data.address,
            memberType: derivedMemberType,
            emergencyContact: {
              name: data.emergencyContactName,
              relationship: data.emergencyContactRelationship,
              phone: `${data.emergencyContactCountryCode}${data.emergencyContactPhone}`,
            },
          },
          clubData: {
            ...(newClubData.churchName
              ? { churchName: newClubData.churchName }
              : {}),
            ...(newClubData.churchId ? { churchId: newClubData.churchId } : {}),
            sections: sections as Record<string, { active: boolean }>,
            inviteCode,
          },
        });

        // Redirigir a página de espera de aprobación del club
        window.location.href = "/pending-club-approval";
      } else if (selectedClub) {
        // ========== FLUJO: UNIRSE A CLUB EXISTENTE ==========

        // Verificar si ya existe una ficha con ese documento (cross-type TI/CC
        // e incluyendo fichas inactivas/huérfanas), vía CF para no exponer la
        // PII de persons al cliente. Decisión por estado del vínculo:
        //   mine         → la ficha es de ESTA cuenta: seguir (la CF de
        //                  aprobación la reutiliza). Antes se bloqueaba también
        //                  este caso y dejaba a la gente de los ex clubes
        //                  fusión sin ningún camino (caso COAL/GEDEÓN).
        //   unlinked     → ficha de roster sin cuenta: el director puede
        //                  inscribirla (mensaje clásico).
        //   linked_other → ficha de OTRA cuenta: bloquear mostrando el correo
        //                  enmascarado y cómo pedir el traslado.
        //   guardian     → la gestiona un acudiente: bloquear con guía.
        // EXCEPCIÓN: si viene a dirigir una sección, su ficha SÍ suele existir
        // y la aprobación la reutiliza — no bloquear nada.
        if (!asSectionDirector) {
          const check = await personService.checkDocumentStatus(
            data.idDocumentType,
            data.idDocumentNumber,
            profile.id,
          );
          if (check.status === "unlinked") {
            setSubmitError(
              "Ya existe una ficha registrada con ese número de documento. No crees una cuenta nueva: pídele al director de tu club que te inscriba (él puede reutilizar tu ficha existente).",
            );
            setSubmitting(false);
            return;
          }
          if (check.status === "linked_other" || check.status === "ambiguous") {
            setSubmitError(
              `Tu documento ya tiene una ficha en Insignia asociada a otra cuenta${
                check.maskedEmail ? ` (${check.maskedEmail})` : ""
              }. Si ese correo es tuyo, inicia sesión con él. Si ya no tienes acceso o era de un familiar, escríbenos a soporte para trasladar tu ficha a esta cuenta — no crees una ficha nueva.`,
            );
            setSubmitting(false);
            return;
          }
          if (check.status === "guardian") {
            setSubmitError(
              "Tu ficha está gestionada por tu padre, madre o acudiente en Insignia. Pídele que gestione tu inscripción desde su cuenta, o escríbenos a soporte si ya debes manejar tu propia cuenta.",
            );
            setSubmitting(false);
            return;
          }
          // "free" y "mine" continúan.
        }

        await joinRequestService.createJoinRequest({
          userId: profile.id,
          userEmail: user.email || "",
          userName: `${data.firstName} ${data.lastName}`,
          userPhotoUrl: user.photoURL || "",
          clubId: selectedClub.id,
          clubName: selectedClub.name,
          clubType: (effectiveSection ||
            (selectedClub.sections
              ? getActiveSections(selectedClub)[0]
              : selectedClub.type) ||
            "conquistadores") as ClubType,
          firstName: data.firstName,
          lastName: data.lastName,
          phone: `${data.phoneCountryCode}${data.phone}`,
          birthDate: new Date(data.birthDate + "T12:00:00"),
          idDocumentType: data.idDocumentType,
          idDocumentNumber: data.idDocumentNumber,
          gender: data.gender,
          bloodType: data.bloodType,
          address: data.address,
          healthInsurance: data.healthInsurance || "",
          memberType: derivedMemberType,
          emergencyContact: {
            name: data.emergencyContactName,
            relationship: data.emergencyContactRelationship,
            phone: `${data.emergencyContactCountryCode}${data.emergencyContactPhone}`,
          },
          // Solicitud de dirección de sección: al aprobarla, la CF asigna el
          // rol y activa la sección. Solo se envían los campos si aplica
          // (Firestore rechaza `undefined`).
          ...(asSectionDirector && requestedSection
            ? {
                requestedRole: "section_director" as const,
                requestedSection,
              }
            : {}),
          // Documentos subidos con la solicitud (foto obligatoria + doc anverso/reverso).
          // La Cloud Function `approveJoinRequest` los copia al Person al aprobar.
          ...(uploadedPhoto
            ? {
                photoUrl: uploadedPhoto.url,
                photoPath: uploadedPhoto.path,
              }
            : {}),
          ...(uploadedDocFront
            ? {
                documentFrontUrl: uploadedDocFront.url,
                documentFrontPath: uploadedDocFront.path,
                documentFrontEncryption: uploadedDocFront.encryption,
                documentFrontContentType: uploadedDocFront.contentType,
              }
            : {}),
          ...(uploadedDocBack
            ? {
                documentBackUrl: uploadedDocBack.url,
                documentBackPath: uploadedDocBack.path,
                documentBackEncryption: uploadedDocBack.encryption,
                documentBackContentType: uploadedDocBack.contentType,
              }
            : {}),
          ...(uploadedEps
            ? {
                epsDocumentUrl: uploadedEps.url,
                epsDocumentPath: uploadedEps.path,
                epsDocumentEncryption: uploadedEps.encryption,
                epsDocumentContentType: uploadedEps.contentType,
              }
            : {}),
          status: "pending",
        });

        sessionStorage.removeItem("pending_invite");
        localStorage.removeItem("pending_invite");
        window.location.href = "/pending-approval";
      }
    } catch (err) {
      console.error("Error:", err);
      const msg = err instanceof Error ? err.message : "";
      // Ya existe solicitud pendiente → redirigir a la pantalla de espera
      // en lugar de mostrar un error genérico ("Intenta de nuevo") que
      // invita a hacer clic otra vez sin cambiar nada.
      if (msg.includes("Ya tienes una solicitud pendiente")) {
        sessionStorage.removeItem("pending_invite");
        localStorage.removeItem("pending_invite");
        window.location.href = "/pending-approval";
        return;
      }
      // F12: mostrar el mensaje del servicio SIEMPRE que sea explicativo.
      //
      // Antes esto era una lista blanca de frases ("ya es miembro activo",
      // "documento"…) y cualquier guarda nueva caía al genérico "Error al
      // enviar la solicitud. Intenta de nuevo.", que no le dice nada a nadie.
      // Pasó de verdad (Mario/GAMA, 2026-08-06): el servicio explicaba
      // exactamente qué hacer y la pantalla lo tapó. La regla ahora es al
      // revés: si el error trae un mensaje legible, se muestra; el genérico
      // queda solo para fallos técnicos (red, permisos, Firebase).
      const isTechnical =
        !msg ||
        msg.length < 15 ||
        /^(FirebaseError|Error:)/i.test(msg) ||
        /permission-denied|unavailable|internal|network|failed to fetch|quota/i.test(
          msg,
        );
      setSubmitError(
        isTechnical
          ? isCreatingClub
            ? "Error al crear el club. Intenta de nuevo."
            : "Error al enviar la solicitud. Intenta de nuevo."
          : msg,
      );
    } finally {
      setSubmitting(false);
    }
  };

  const [confirmingSignOut, setConfirmingSignOut] = useState(false);

  const handleSignOut = async () => {
    if (!confirmingSignOut) {
      setConfirmingSignOut(true);
      setTimeout(() => setConfirmingSignOut(false), 3000);
      return;
    }
    await signOut();
    navigate("/login");
  };

  // ============================================================
  // FLUJO USUARIO EXISTENTE CON INVITE (cambio de club)
  // ============================================================
  const isExistingUserWithInvite = !!inviteFromUrl && !!profile?.primaryRole;

  const [changeClubMode, setChangeClubMode] = useState<
    "prompt" | "form" | "done"
  >(isExistingUserWithInvite ? "prompt" : "form");

  const currentClubName =
    profile?.directorOfClub?.name ||
    profile?.memberships?.[0]?.clubName ||
    profile?.scopeName ||
    "tu club actual";

  if (isExistingUserWithInvite && changeClubMode === "prompt") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-primary-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8">
          <div className="flex justify-center mb-6">
            <img
              src="/branding/insignia_icon.svg"
              alt="Insignia"
              className="w-12 h-12"
            />
          </div>
          <h2 className="text-2xl font-bold text-center text-gray-900 mb-2">
            ¡Tienes una invitación!
          </h2>
          <p className="text-center text-gray-500 text-sm mb-6">
            Has iniciado sesión como <strong>{profile?.displayName}</strong>
          </p>

          {/* Club actual */}
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-3">
            <p className="text-xs text-gray-400 mb-1 uppercase tracking-wide">
              Club actual
            </p>
            <p className="font-semibold text-gray-700">{currentClubName}</p>
          </div>

          {/* Arrow */}
          <div className="flex justify-center my-2">
            <ChevronRight className="w-5 h-5 text-gray-400" />
          </div>

          {/* Club de destino */}
          <div className="bg-primary-50 border-2 border-primary-200 rounded-xl p-4 mb-6">
            <p className="text-xs text-primary-500 mb-1 uppercase tracking-wide font-medium">
              Club al que te invitan
            </p>
            {selectedClub ? (
              <p className="font-bold text-primary-800 text-lg">
                {selectedClub.name}
              </p>
            ) : inviteError ? (
              <p className="text-red-600 text-sm">{inviteError}</p>
            ) : (
              <p className="text-gray-400 text-sm animate-pulse">Cargando…</p>
            )}
          </div>

          <div className="space-y-3">
            <Button
              className="w-full"
              onClick={() => setChangeClubMode("form")}
              disabled={!selectedClub || !!inviteError}
            >
              Solicitar unirme a este club
            </Button>
            <Button
              variant="secondary"
              className="w-full"
              onClick={() => navigate("/dashboard")}
            >
              Ir al inicio
            </Button>
          </div>

          <p className="text-xs text-center text-gray-400 mt-4">
            Puedes seguir usando tu cuenta actual mientras se aprueba la
            solicitud.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-primary-50">
      {/* Header */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <img
              src="/branding/insignia_icon.svg"
              alt="Insignia"
              className="w-8 h-8 shrink-0"
            />
            <div className="min-w-0">
              <h1 className="text-xl font-bold text-gray-900 truncate">
                {isExistingUserWithInvite
                  ? "Solicitar cambio de club"
                  : "Completa tu registro"}
              </h1>
              {user?.email && (
                <p className="text-xs text-gray-500 truncate flex items-center gap-1">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-green-500" />
                  Sesión:{" "}
                  <span className="font-medium text-gray-700">
                    {user.email}
                  </span>
                </p>
              )}
            </div>
          </div>
          <button
            onClick={handleSignOut}
            className={`flex items-center gap-2 text-sm transition-colors shrink-0 ${
              confirmingSignOut
                ? "text-red-600 font-medium"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <LogOut className="w-4 h-4" />
            {confirmingSignOut ? "¿Salir? Pulsa de nuevo" : "Salir"}
          </button>
        </div>
      </div>

      {/* Stepper */}
      <div className="max-w-3xl mx-auto px-4 pt-6 pb-2">
        <div className="flex items-center justify-between">
          {/* Cuando se llega por invite, ocultar el paso "Buscar Club" */}
          {STEPS.filter((_, i) => !inviteFromUrl || i > 0).map((s, idx) => {
            const i = inviteFromUrl ? idx + 1 : idx; // índice real
            const Icon = s.icon;
            const isActive = i === step;
            const isDone = i < step;
            return (
              <div key={s.title} className="flex items-center flex-1">
                <div className="flex flex-col items-center flex-1">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                      isDone
                        ? "bg-green-500 text-white"
                        : isActive
                          ? "bg-primary-600 text-white"
                          : "bg-gray-200 text-gray-500"
                    }`}
                  >
                    {isDone ? (
                      <CheckCircle className="w-5 h-5" />
                    ) : (
                      <Icon className="w-5 h-5" />
                    )}
                  </div>
                  <span
                    className={`text-xs mt-1 ${isActive ? "text-primary-700 font-medium" : "text-gray-500"}`}
                  >
                    {s.title}
                  </span>
                </div>
                {idx <
                  (inviteFromUrl ? STEPS.length - 2 : STEPS.length - 1) && (
                  <div
                    className={`h-0.5 flex-1 mx-2 -mt-4 ${i < step ? "bg-green-500" : "bg-gray-200"}`}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 py-6">
        {/* Error si falla resolución del invite */}
        {inviteError && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2 text-red-700 text-sm">
            <AlertCircle className="w-4 h-4 shrink-0" />
            {inviteError}
          </div>
        )}

        {/* Club resuelto desde invite URL (visible en step 1) */}
        {inviteFromUrl && selectedClub && step === 1 && (
          <div className="mb-4 flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl px-4 py-3">
            {selectedClub.logoUrl ? (
              <img
                src={selectedClub.logoUrl}
                alt={selectedClub.name}
                className="w-10 h-10 rounded-xl object-cover shrink-0 border border-green-200"
              />
            ) : (
              <CheckCircle className="w-5 h-5 text-green-600 shrink-0" />
            )}
            <div className="min-w-0">
              <p className="text-sm font-semibold text-green-800">
                {selectedClub.name}
              </p>
              <p className="text-xs text-green-600">
                Club confirmado · Completa tus datos para enviar la solicitud
              </p>
              {/* Salida del secuestro: un código de invitación guardado en el
                  navegador preselecciona el club y se salta el paso 0, así que
                  quien recibió un enlace hace meses —o cuyo club se fusionó y
                  cambió de nombre— no encontraba cómo elegir otra cosa ni
                  crear el suyo. Le pasó a una directora el 2026-08-06. */}
              <button
                type="button"
                onClick={limpiarInvitacion}
                className="text-xs text-green-700 underline underline-offset-2 mt-1 hover:text-green-900"
              >
                Este no es mi club — quiero elegir otro o crear el mío
              </button>
            </div>
          </div>
        )}

        {step === 0 && (
          <StepFindClub
            selectedClub={selectedClub}
            initialCode={inviteFromUrl}
            onSelectClub={(club) => {
              setSelectedClub(club);
              setIsCreatingClub(false);
              setNewClubData(null);
              // Al cambiar de club la sección elegida deja de valer (en otro
              // club puede estar ocupada). El camino de director SÍ se
              // mantiene: solo lo cancela el enlace "solo quiero unirme como
              // miembro" — si no, pulsar "Cambiar" lo perdería en silencio y
              // acabaría enviando una solicitud de miembro normal.
              if (club?.id !== selectedClub?.id) setRequestedSection(null);
              // Reset del "vengo como miembro" al cambiar de club para
              // volver a exigir la elección de rol.
              setRoleConfirmedAsMember(false);
            }}
            isCreatingClub={isCreatingClub}
            newClubData={newClubData}
            onCreateClub={(data) => {
              setIsCreatingClub(true);
              setNewClubData(data);
              setSelectedClub(null);
              setAsSectionDirector(false);
              setRequestedSection(null);
            }}
            onCancelCreate={() => {
              setIsCreatingClub(false);
              setNewClubData(null);
            }}
            asSectionDirector={asSectionDirector}
            requestedSection={requestedSection}
            onSectionDirectorFlow={(on) => {
              setAsSectionDirector(on);
              if (!on) setRequestedSection(null);
              // Al declararse director cancelamos la confirmación de
              // "vengo como miembro"; al desactivarla vuelve a exigir la
              // elección explícita en la siguiente iteración.
              if (on) setRoleConfirmedAsMember(false);
            }}
            onRequestedSection={setRequestedSection}
            roleConfirmedAsMember={roleConfirmedAsMember}
            onConfirmMember={() => setRoleConfirmedAsMember(true)}
          />
        )}
        {step === 1 && (
          <StepPersonalData
            form={form}
            age={age}
            allowedClubType={allowedClubType}
            selectedClub={selectedClub}
            derivedSection={derivedSection}
            userId={user?.uid ?? ""}
            uploadedPhoto={uploadedPhoto}
            setUploadedPhoto={setUploadedPhoto}
            uploadedDocFront={uploadedDocFront}
            setUploadedDocFront={setUploadedDocFront}
            uploadedDocBack={uploadedDocBack}
            setUploadedDocBack={setUploadedDocBack}
            uploadedEps={uploadedEps}
            setUploadedEps={setUploadedEps}
          />
        )}
        {step === 2 && (
          <StepConfirm
            formValues={formValues}
            selectedClub={selectedClub}
            age={age}
            derivedSection={derivedSection}
            effectiveSection={effectiveSection}
            sectionAdjusted={sectionAdjusted}
            isCreatingClub={isCreatingClub}
            newClubData={newClubData}
            requestedSection={requestedSection}
          />
        )}

        {submitError && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {submitError}
          </div>
        )}

        {/* Navigation */}
        <div className="flex justify-between mt-6">
          <Button
            variant="secondary"
            onClick={handleBack}
            disabled={step === 0}
            className={
              step === 0 || (inviteFromUrl && step === 1) ? "invisible" : ""
            }
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Atrás
          </Button>

          {step < 2 ? (
            <Button
              onClick={handleNext}
              disabled={
                step === 0 &&
                ((!selectedClub && !isCreatingClub) ||
                  // Declaró que viene a dirigir una sección pero aún no dijo cuál
                  (asSectionDirector && !requestedSection) ||
                  // Seleccionó club pero aún no aclaró si viene como miembro
                  // o como director/a — evita que el flujo silencioso de
                  // "miembro" atrape a los directores que no vieron el botón.
                  (!!selectedClub &&
                    !asSectionDirector &&
                    !roleConfirmedAsMember))
              }
            >
              Siguiente
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={submitting}>
              {submitting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : isCreatingClub ? (
                <Plus className="w-4 h-4 mr-2" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              {isCreatingClub ? "Crear Club" : "Enviar Solicitud"}
            </Button>
          )}
        </div>
      </div>

      {/* Build version */}
      <footer className="py-3 text-center">
        <p className="text-[10px] text-gray-400 font-mono">
          Build: {__BUILD_TIMESTAMP__}
        </p>
      </footer>
    </div>
  );
}

// ============================================
// STEP 1: FIND CLUB
// ============================================

function StepFindClub({
  selectedClub,
  onSelectClub,
  isCreatingClub,
  newClubData,
  onCreateClub,
  onCancelCreate,
  initialCode = "",
  asSectionDirector,
  requestedSection,
  onSectionDirectorFlow,
  onRequestedSection,
  roleConfirmedAsMember,
  onConfirmMember,
}: {
  selectedClub: Club | null;
  onSelectClub: (club: Club | null) => void;
  isCreatingClub: boolean;
  newClubData: NewClubData | null;
  onCreateClub: (data: NewClubData) => void;
  onCancelCreate: () => void;
  initialCode?: string;
  /** El aspirante declaró que dirige una sección de un club ya registrado. */
  asSectionDirector: boolean;
  requestedSection: ClubType | null;
  onSectionDirectorFlow: (on: boolean) => void;
  onRequestedSection: (section: ClubType | null) => void;
  /** El aspirante confirmó explícitamente que viene como miembro. */
  roleConfirmedAsMember: boolean;
  onConfirmMember: () => void;
}) {
  const [mode, setMode] = useState<"code" | "search" | "create">(
    isCreatingClub ? "create" : "code",
  );
  const [showDirectorConfirm, setShowDirectorConfirm] = useState(false);
  const [code, setCode] = useState(initialCode);
  const [searchText, setSearchText] = useState("");
  const [results, setResults] = useState<Club[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Estado local para crear club
  const [clubName, setClubName] = useState(newClubData?.name || "");
  const [churchName, setChurchName] = useState(newClubData?.churchName || "");
  const [churchId, setChurchId] = useState(newClubData?.churchId || "");
  const [churchSearch, setChurchSearch] = useState("");
  const [selectedAssociationId, setSelectedAssociationId] = useState(
    newClubData?.associationId || "",
  );
  const [associations, setAssociations] = useState<Association[]>([]);
  const [churches, setChurches] = useState<Church[]>([]);
  const [loadingChurches, setLoadingChurches] = useState(false);
  const [districts, setDistricts] = useState<District[]>([]);
  const [sectionsState, setSectionsState] = useState(
    newClubData?.sections || {
      aventureros: false,
      conquistadores: true,
      // Abierto a propósito: en este tipo quedan TODOS los mayores de 16 —
      // la directiva, los consejeros y el propio director. Si nace cerrado,
      // esa gente entra al club y no se ve.
      guias_mayores: true,
    },
  );

  // Prevención de clubes duplicados: si ya existe un club con nombre similar,
  // avisar y ofrecer unirse en vez de crear otro (caso típico: el club ya está
  // registrado y el aspirante viene a dirigir una sección nueva, p.ej. Aventureros).
  const [similarClubs, setSimilarClubs] = useState<Club[]>([]);
  const [dupConfirmed, setDupConfirmed] = useState(false);

  useEffect(() => {
    // Se compara por NOMBRE y por IGLESIA: la regla es una iglesia = un club,
    // así que un nombre distinto en la misma iglesia también es duplicado.
    if (mode !== "create" || (clubName.trim().length < 3 && !churchId)) {
      setSimilarClubs([]);
      setDupConfirmed(false);
      return;
    }
    let active = true;
    const timer = setTimeout(() => {
      clubService
        .findPossibleDuplicates({
          name: clubName,
          churchId,
          churchName,
        })
        .then((clubs) => {
          if (!active) return;
          setSimilarClubs(clubs);
          setDupConfirmed(false);
        })
        .catch(() => {});
    }, 450);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [clubName, churchId, churchName, mode]);

  const dupBlocked = similarClubs.length > 0 && !dupConfirmed;

  // Si llega un código por URL, el club ya se resolvió en SetupPage — no hacer nada aquí
  // Cargar asociaciones cuando se entra en modo crear.
  // Por ahora solo se permite registrar clubes en la Asociación del Oriente
  // Colombiano (AOC). Las demás asociaciones se ocultan del selector.
  useEffect(() => {
    if (mode === "create" && associations.length === 0) {
      associationService
        .getAll()
        .then((list) => {
          const onlyAoc = list.filter(
            (a) =>
              a.name.toLowerCase().includes("oriente") ||
              (a as any).code === "AOC",
          );
          setAssociations(onlyAoc);
          if (!selectedAssociationId && onlyAoc.length > 0) {
            setSelectedAssociationId(onlyAoc[0].id);
          }
        })
        .catch(console.error);
    }
  }, [mode]);

  // Cargar iglesias cuando cambia la asociación
  useEffect(() => {
    if (!selectedAssociationId) {
      setChurches([]);
      setDistricts([]);
      setChurchId("");
      setChurchName("");
      setChurchSearch("");
      return;
    }
    setLoadingChurches(true);
    Promise.all([
      churchService.getByAssociation(selectedAssociationId),
      districtService.getByAssociation(selectedAssociationId).catch(() => []),
    ])
      .then(([churchList, districtList]) => {
        setChurches(churchList);
        setDistricts(districtList);
      })
      .catch(console.error)
      .finally(() => setLoadingChurches(false));
  }, [selectedAssociationId]);

  // Map districtId → district name para mostrar junto a la iglesia
  const districtNameById = useMemo(() => {
    const map = new Map<string, string>();
    districts.forEach((d) => map.set(d.id, d.name));
    return map;
  }, [districts]);

  const getChurchDistrictLabel = (church: Church): string | null => {
    // Primero usamos el nombre desnormalizado guardado en el doc de la iglesia
    if (church.districtName) return church.districtName;
    // Fallback: lookup en la colección districts (si existe)
    if (!church.districtId) return null;
    return districtNameById.get(church.districtId) ?? null;
  };

  const hasAtLeastOneSection =
    sectionsState.aventureros ||
    sectionsState.conquistadores ||
    sectionsState.guias_mayores;

  // Sincronizar datos del nuevo club hacia arriba.
  // Si hay clubes similares sin confirmar, NO sincronizar (bloquea el Siguiente).
  useEffect(() => {
    if (
      mode === "create" &&
      clubName.trim() &&
      hasAtLeastOneSection &&
      selectedAssociationId &&
      !dupBlocked
    ) {
      onCreateClub({
        name: clubName.trim(),
        churchName: churchName.trim(),
        churchId: churchId,
        associationId: selectedAssociationId,
        sections: sectionsState,
      });
    } else if (mode === "create" && dupBlocked) {
      onCancelCreate();
    }
  }, [
    clubName,
    churchName,
    churchId,
    selectedAssociationId,
    sectionsState,
    mode,
    dupBlocked,
  ]);

  const handleCodeSearch = async () => {
    if (code.length < 6) return;
    setLoading(true);
    setError(null);
    try {
      const club = await clubService.getByInviteCode(code);
      if (club) {
        onSelectClub(club);
        setResults([]);
      } else {
        setError("No se encontró un club con ese código.");
      }
    } catch {
      setError("Error al buscar. Intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  const handleNameSearch = useCallback(async () => {
    if (searchText.length < 2) {
      setResults([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const clubs = await clubService.searchByName(searchText);
      setResults(clubs);
      if (clubs.length === 0) {
        setError("No se encontraron clubes con ese nombre.");
      }
    } catch {
      setError("Error al buscar.");
    } finally {
      setLoading(false);
    }
  }, [searchText]);

  // Debounce search
  useEffect(() => {
    if (mode !== "search" || searchText.length < 2) return;
    const timer = setTimeout(handleNameSearch, 400);
    return () => clearTimeout(timer);
  }, [searchText, mode, handleNameSearch]);

  return (
    <Card className="border-0 shadow-lg">
      <CardHeader>
        <CardTitle>
          {mode === "create"
            ? "Crear un club nuevo"
            : "¿A qué club deseas unirte?"}
        </CardTitle>
        <p className="text-sm text-gray-500 mt-1">
          {mode === "create"
            ? "Completa los datos de tu club. Serás registrado como director."
            : "Ingresa el código de invitación que te dieron o busca tu club por nombre."}
        </p>
      </CardHeader>
      <CardContent>
        {/* Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          <button
            onClick={() => {
              setMode("code");
              setError(null);
              onCancelCreate();
            }}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              mode === "code"
                ? "bg-primary-100 text-primary-700"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            <Hash className="w-4 h-4" />
            Código de Invitación
          </button>
          <button
            onClick={() => {
              setMode("search");
              setError(null);
              onCancelCreate();
            }}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              mode === "search"
                ? "bg-primary-100 text-primary-700"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            <Search className="w-4 h-4" />
            Buscar por Nombre
          </button>
          <button
            onClick={() => setShowDirectorConfirm(true)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              mode === "create"
                ? "bg-emerald-100 text-emerald-700"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            <Shield className="w-4 h-4" />
            Soy Director
          </button>
        </div>

        {/* Modal de confirmación: "¿Realmente eres director?" */}
        <Modal
          isOpen={showDirectorConfirm}
          onClose={() => setShowDirectorConfirm(false)}
          title="Eres director — ¿tu club ya está registrado?"
          size="md"
        >
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              En la plataforma, una iglesia tiene <b>un solo club</b> y dentro
              van sus <b>tipos de club</b>: Aventureros, Conquistadores y Guías
              Mayores. Elige según tu caso:
            </p>

            {/* Caso A: el club YA existe y viene a dirigir un tipo de club */}
            <button
              type="button"
              onClick={() => {
                setShowDirectorConfirm(false);
                setMode("search");
                setError(null);
                onCancelCreate();
                onSectionDirectorFlow(true);
              }}
              className="w-full text-left p-4 rounded-xl border-2 border-primary-300 bg-primary-50 hover:bg-primary-100 transition-colors"
            >
              <p className="font-semibold text-primary-900 flex items-center gap-2">
                <Users className="w-4 h-4 shrink-0" />
                Mi club ya está en la plataforma y yo dirijo Aventureros,
                Conquistadores o Guías Mayores
              </p>
              <p className="text-xs text-primary-700 mt-1">
                Por ejemplo: el club de tu iglesia ya tiene Conquistadores y tú
                vas a dirigir Aventureros. Buscas tu club, dices qué tipo de
                club diriges y el director general o la asociación te aprueba.{" "}
                <b>No se crea un club nuevo.</b>
              </p>
            </button>

            {/* Caso B: el club no existe todavía */}
            <button
              type="button"
              onClick={() => {
                setShowDirectorConfirm(false);
                setMode("create");
                setError(null);
                onSelectClub(null);
                onSectionDirectorFlow(false);
              }}
              className="w-full text-left p-4 rounded-xl border-2 border-gray-200 bg-white hover:bg-gray-50 transition-colors"
            >
              <p className="font-semibold text-gray-900 flex items-center gap-2">
                <Plus className="w-4 h-4 shrink-0" />
                Mi club NO está registrado todavía — voy a crearlo
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Solo si tu iglesia no tiene ningún club en la plataforma.
                Requiere ser mayor de edad y que tu pastor/iglesia te haya
                asignado. La asociación revisa la solicitud.
              </p>
            </button>

            <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <p className="text-xs text-amber-900">
                Si no eres director y solo quieres{" "}
                <strong>unirte como miembro</strong>, cierra esto y usa{" "}
                <strong>“Código de Invitación”</strong> o{" "}
                <strong>“Buscar por Nombre”</strong>.
              </p>
            </div>

            <div className="flex justify-end pt-1">
              <Button
                variant="secondary"
                onClick={() => setShowDirectorConfirm(false)}
              >
                Cancelar
              </Button>
            </div>
          </div>
        </Modal>

        {/* Banner del camino "dirijo un tipo de club de un club ya registrado" */}
        {asSectionDirector && (
          <div className="mb-4 rounded-xl border-2 border-primary-300 bg-primary-50 p-4">
            <p className="text-sm font-bold text-primary-900 flex items-center gap-2">
              <Users className="w-4 h-4 shrink-0" />
              Vas a unirte como director/a
            </p>
            <p className="text-xs text-primary-700 mt-1">
              {selectedClub
                ? "Indica abajo qué tipo de club diriges (Aventureros, Conquistadores o Guías Mayores). Tu solicitud le llegará al director general del club y al administrador de la asociación."
                : "Busca tu club por nombre y selecciónalo. Después eliges qué tipo de club diriges."}
            </p>
            <button
              type="button"
              onClick={() => onSectionDirectorFlow(false)}
              className="mt-2 text-xs text-primary-700 underline hover:text-primary-900"
            >
              No, solo quiero unirme como miembro
            </button>
          </div>
        )}

        {mode === "code" ? (
          <div className="space-y-2">
            <div className="flex gap-3">
              <Input
                placeholder="Ej: ABC123"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                className="flex-1 font-mono text-lg tracking-wider uppercase"
              />
              <Button
                onClick={handleCodeSearch}
                disabled={code.length < 6 || loading}
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  "Buscar"
                )}
              </Button>
            </div>
            <p className="text-xs text-gray-500 flex items-center gap-1">
              <Info className="w-3.5 h-3.5 shrink-0" />
              Pídele el código de 6 caracteres al director de tu club. Si no lo
              tienes, busca por nombre.
            </p>
          </div>
        ) : mode === "search" ? (
          <div className="relative">
            <Input
              placeholder="Escribe el nombre del club..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="pl-9"
            />
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Search className="w-4 h-4" />
              )}
            </span>
          </div>
        ) : (
          /* CREATE CLUB FORM */
          <div className="space-y-4">
            <Input
              label="Nombre del Club *"
              placeholder="Ej: Centinelas, Águilas Reales..."
              value={clubName}
              onChange={(e) => setClubName(e.target.value)}
            />

            {/* Prevención de duplicados: el club puede que YA exista */}
            {similarClubs.length > 0 && (
              <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-4 space-y-3">
                <p className="text-sm font-bold text-amber-800">
                  ⚠️ Ya{" "}
                  {similarClubs.length === 1
                    ? "existe un club que podría ser el tuyo"
                    : "existen clubes que podrían ser el tuyo"}
                </p>
                <p className="text-xs text-amber-700">
                  Si tu club es uno de estos, <b>no crees uno nuevo</b>: una
                  iglesia tiene un solo club, y adentro van sus tipos de club.
                  Pulsa <b>“Este es mi club”</b> y dinos cuál diriges — al
                  aprobarte quedarás como <b>director/a de Aventureros</b> (o
                  del tipo que dirijas) sin duplicar el club.
                </p>
                <div className="space-y-2">
                  {similarClubs.slice(0, 4).map((c) => (
                    <div
                      key={c.id}
                      className="flex items-center justify-between gap-3 bg-white border border-amber-200 rounded-lg px-3 py-2"
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">
                          {c.name}
                        </p>
                        {(c as { churchName?: string }).churchName && (
                          <p className="text-xs text-gray-500 truncate">
                            {(c as { churchName?: string }).churchName}
                          </p>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          onSelectClub(c);
                          setMode("search");
                          setError(null);
                          // Venía de "Soy Director": no es un miembro más, viene
                          // a dirigir un tipo de club del que ya existe.
                          onSectionDirectorFlow(true);
                        }}
                        className="shrink-0 px-3 py-1.5 text-xs font-semibold text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
                      >
                        Este es mi club
                      </button>
                    </div>
                  ))}
                </div>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={dupConfirmed}
                    onChange={(e) => setDupConfirmed(e.target.checked)}
                    className="mt-0.5 rounded border-amber-400 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-xs text-amber-800">
                    Confirmo que mi club <b>NO</b> es ninguno de los anteriores
                    y quiero registrar un club nuevo con este nombre.
                  </span>
                </label>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Asociación / Misión *
              </label>
              {associations.length === 0 ? (
                <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Cargando asociaciones...
                </div>
              ) : (
                <select
                  value={selectedAssociationId}
                  onChange={(e) => setSelectedAssociationId(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Selecciona una asociación...</option>
                  {associations.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Iglesia (opcional)
              </label>
              {loadingChurches ? (
                <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Cargando iglesias...
                </div>
              ) : selectedAssociationId ? (
                <>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Buscar iglesia..."
                      value={churchSearch}
                      onChange={(e) => {
                        setChurchSearch(e.target.value);
                        if (!e.target.value) {
                          setChurchId("");
                          setChurchName("");
                        }
                      }}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 pl-9 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                    <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    {churchId && (
                      <button
                        type="button"
                        onClick={() => {
                          setChurchId("");
                          setChurchName("");
                          setChurchSearch("");
                        }}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                  {churchSearch.length >= 2 &&
                    !churchId &&
                    (() => {
                      // Normaliza para búsqueda insensible a tildes/acentos
                      const norm = (s: string) =>
                        (s || "")
                          .toLowerCase()
                          .normalize("NFD")
                          .replace(/[\u0300-\u036f]/g, "");
                      const q = norm(churchSearch);
                      const matches = churches
                        .filter((c) => {
                          const district = norm(
                            getChurchDistrictLabel(c) || "",
                          );
                          return (
                            norm(c.name).includes(q) || district.includes(q)
                          );
                        })
                        .slice(0, 8);
                      return matches.length > 0 ? (
                        <ul className="mt-1 border border-gray-200 rounded-lg divide-y divide-gray-100 overflow-hidden max-h-44 overflow-y-auto shadow-sm">
                          {matches.map((c) => {
                            const districtLabel = getChurchDistrictLabel(c);
                            return (
                              <li key={c.id}>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setChurchId(c.id);
                                    setChurchName(c.name);
                                    setChurchSearch(c.name);
                                  }}
                                  className="w-full text-left px-3 py-2 text-sm hover:bg-primary-50 text-gray-800"
                                >
                                  <div className="flex items-center justify-between gap-2">
                                    <span className="truncate">{c.name}</span>
                                    {districtLabel && (
                                      <span className="text-xs text-gray-500 shrink-0">
                                        {districtLabel}
                                      </span>
                                    )}
                                  </div>
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      ) : (
                        <p className="text-xs text-gray-500 mt-1">
                          Sin resultados para "{churchSearch}"
                        </p>
                      );
                    })()}
                  {churchId && (
                    <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                      <CheckCircle className="w-3.5 h-3.5" />
                      <span>{churchName}</span>
                      {(() => {
                        const selected = churches.find(
                          (c) => c.id === churchId,
                        );
                        const districtLabel = selected
                          ? getChurchDistrictLabel(selected)
                          : null;
                        return districtLabel ? (
                          <span className="text-gray-500">
                            · {districtLabel}
                          </span>
                        ) : null;
                      })()}
                    </p>
                  )}
                </>
              ) : (
                <p className="text-sm text-gray-400 py-1">
                  Selecciona primero una asociación
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tipos de club *
              </label>
              <p className="text-xs text-gray-500 mb-3">
                Selecciona los que tendrá tu club. Puedes abrir más después.
              </p>
              <div className="space-y-2">
                {[
                  {
                    key: "aventureros" as const,
                    label: "Aventureros",
                    desc: "6 a 9 años",
                  },
                  {
                    key: "conquistadores" as const,
                    label: "Conquistadores",
                    desc: "10 a 15 años",
                  },
                  {
                    key: "guias_mayores" as const,
                    label: "Guías Mayores",
                    desc: "16+ años · aquí quedan tu directiva y consejeros",
                  },
                ].map(({ key, label, desc }) => (
                  <label
                    key={key}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      sectionsState[key]
                        ? "bg-primary-50 border-primary-300"
                        : "bg-white border-gray-200 hover:bg-gray-50"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={sectionsState[key]}
                      onChange={(e) =>
                        setSectionsState((prev) => ({
                          ...prev,
                          [key]: e.target.checked,
                        }))
                      }
                      className="w-4 h-4 text-primary-600 rounded border-gray-300"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-900">
                        {label}
                      </span>
                      <span className="text-xs text-gray-500 ml-2">
                        ({desc})
                      </span>
                    </div>
                  </label>
                ))}
              </div>
              {!hasAtLeastOneSection && (
                <p className="text-xs text-red-500 mt-2">
                  Debes seleccionar al menos una sección.
                </p>
              )}
              {!selectedAssociationId && (
                <p className="text-xs text-red-500 mt-2">
                  Debes seleccionar una asociación.
                </p>
              )}
            </div>

            {/* Preview */}
            {clubName.trim() &&
              hasAtLeastOneSection &&
              selectedAssociationId && (
                <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                    <span className="font-medium text-emerald-800">
                      Club listo para crear
                    </span>
                  </div>
                  <p className="text-emerald-700 font-semibold mt-1">
                    {clubName.trim()}
                  </p>
                  <p className="text-sm text-emerald-600">
                    {[
                      sectionsState.aventureros && "Aventureros",
                      sectionsState.conquistadores && "Conquistadores",
                      sectionsState.guias_mayores && "Guías Mayores",
                    ]
                      .filter(Boolean)
                      .join(", ")}
                    {churchName.trim() && ` • ${churchName.trim()}`}
                  </p>
                  <p className="text-xs text-emerald-600 mt-1">
                    Serás registrado como Director del club.
                  </p>
                </div>
              )}
          </div>
        )}

        {error && (
          <p className="mt-3 text-sm text-red-600 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            {error}
          </p>
        )}

        {/* Search results */}
        {results.length > 0 && (
          <div className="mt-4 space-y-2 max-h-64 overflow-y-auto">
            {results.map((club) => (
              <button
                key={club.id}
                onClick={() => {
                  onSelectClub(club);
                  setResults([]);
                  setSearchText("");
                }}
                className={`w-full text-left p-3 rounded-lg border transition-colors hover:bg-primary-50 hover:border-primary-300 ${
                  selectedClub?.id === club.id
                    ? "bg-primary-50 border-primary-300"
                    : "bg-white border-gray-200"
                }`}
              >
                <div className="font-medium text-gray-900">{club.name}</div>
                <div className="text-sm text-gray-500">
                  {getActiveSections(club)
                    .map((s) => CLUB_TYPE_LABELS[s as ClubType])
                    .join(", ")}
                  {club.churchName ? ` • ${club.churchName}` : ""}
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Selected club */}
        {selectedClub && (
          <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-xl">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="font-medium text-green-800">
                    Club seleccionado
                  </span>
                </div>
                <p className="text-green-700 font-semibold mt-1">
                  {selectedClub.name}
                </p>
                <p className="text-sm text-green-600">
                  {getActiveSections(selectedClub)
                    .map((s) => CLUB_TYPE_LABELS[s as ClubType])
                    .join(", ")}
                  {selectedClub.churchName && ` • ${selectedClub.churchName}`}
                </p>
              </div>
              <button
                onClick={() => onSelectClub(null)}
                className="text-sm text-green-600 hover:text-green-800 underline"
              >
                Cambiar
              </button>
            </div>
          </div>
        )}

        {/* Pregunta bloqueante: ¿miembro o director/a?
            Sin esto los directores que llegaron por "Buscar/Código" avanzaban
            silenciosamente como miembros y su solicitud quedaba mal marcada. */}
        {selectedClub && !asSectionDirector && !roleConfirmedAsMember && (
          <div className="mt-4 p-4 rounded-xl border-2 border-amber-400 bg-amber-50">
            <p className="text-sm font-bold text-amber-900 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 shrink-0" />
              ¿Cómo vas a participar en {selectedClub.name}?
            </p>
            <p className="text-xs text-amber-800 mt-1">
              Elige una opción para continuar. Si eres director/a de un tipo de
              club y eliges "miembro" por error, luego tendrás que volver a
              hacer todo el proceso.
            </p>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <button
                type="button"
                onClick={onConfirmMember}
                className="text-left p-3 rounded-lg border-2 border-gray-200 bg-white hover:border-gray-400 hover:bg-gray-50 transition-colors"
              >
                <p className="font-semibold text-gray-900 flex items-center gap-2">
                  <Users className="w-4 h-4 shrink-0" />
                  Vengo como miembro
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Me integro al club como participante (Aventurero,
                  Conquistador, Guía Mayor, líder, staff, papá/mamá…).
                </p>
              </button>
              <button
                type="button"
                onClick={() => onSectionDirectorFlow(true)}
                className="text-left p-3 rounded-lg border-2 border-primary-400 bg-white hover:bg-primary-50 transition-colors"
              >
                <p className="font-semibold text-primary-900 flex items-center gap-2">
                  <Shield className="w-4 h-4 shrink-0" />
                  Soy director/a de un tipo de club
                </p>
                <p className="text-xs text-primary-700 mt-1">
                  Dirijo Aventureros, Conquistadores o Guías Mayores dentro de
                  este club. Mi solicitud requiere aprobación del director
                  general o de la asociación.
                </p>
              </button>
            </div>
          </div>
        )}

        {/* Selector del tipo de club a dirigir */}
        {asSectionDirector && selectedClub && (
          <div className="mt-4 p-4 rounded-xl border-2 border-primary-300 bg-white">
            <p className="text-sm font-semibold text-gray-900">
              ¿Qué tipo de club diriges en {selectedClub.name}?
            </p>
            <div className="mt-3 space-y-2">
              {(
                ["aventureros", "conquistadores", "guias_mayores"] as ClubType[]
              ).map((s) => {
                const cfg = selectedClub.sections?.[s];
                // Si ese tipo de club ya tiene director no se puede reclamar
                // desde aquí: aprobar la solicitud destituiría al actual. Ese
                // cambio lo hace el director general / la asociación a conciencia.
                const takenBy = cfg?.directorName || null;
                const taken = !!(cfg?.directorId || cfg?.directorUserId);
                const checked = requestedSection === s;
                return (
                  <label
                    key={s}
                    className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${
                      taken
                        ? "border-gray-200 bg-gray-50 cursor-not-allowed opacity-70"
                        : checked
                          ? "border-primary-400 bg-primary-50 cursor-pointer"
                          : "border-gray-200 hover:border-primary-300 cursor-pointer"
                    }`}
                  >
                    <input
                      type="radio"
                      name="requestedSection"
                      className="mt-1"
                      disabled={taken}
                      checked={checked}
                      onChange={() => onRequestedSection(s)}
                    />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900">
                        {CLUB_TYPE_LABELS[s]}
                      </p>
                      <p className="text-xs text-gray-500">
                        {taken
                          ? `Ya tiene director/a${takenBy ? `: ${takenBy}` : ""}. Si hubo un cambio, pídele a la asociación que lo actualice.`
                          : cfg?.active
                            ? "Abierto en este club, sin director/a asignado/a."
                            : "Aún no está abierto en este club — se abrirá cuando aprueben tu solicitud."}
                      </p>
                    </div>
                  </label>
                );
              })}
            </div>
            <p className="mt-3 text-xs text-gray-500 flex items-start gap-1">
              <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              Si te aprueban, entrarás directo al panel de tu club. Si te
              equivocas de club o de tipo, la solicitud será rechazada.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================
// STEP 2: PERSONAL DATA
// ============================================

function StepPersonalData({
  form,
  age,
  allowedClubType: _allowedClubType,
  selectedClub: _selectedClub,
  derivedSection,
  userId,
  uploadedPhoto,
  setUploadedPhoto,
  uploadedDocFront,
  setUploadedDocFront,
  uploadedDocBack,
  setUploadedDocBack,
  uploadedEps,
  setUploadedEps,
}: {
  form: ReturnType<typeof useForm<PersonalForm>>;
  age: number | null;
  allowedClubType: string | null;
  selectedClub: Club | null;
  derivedSection: string | null;
  userId: string;
  uploadedPhoto: { url: string; path: string } | null;
  setUploadedPhoto: (v: { url: string; path: string } | null) => void;
  uploadedDocFront: {
    url: string;
    path: string;
    encryption: EncryptionMeta;
    contentType: string;
  } | null;
  setUploadedDocFront: (
    v: {
      url: string;
      path: string;
      encryption: EncryptionMeta;
      contentType: string;
    } | null,
  ) => void;
  uploadedDocBack: {
    url: string;
    path: string;
    encryption: EncryptionMeta;
    contentType: string;
  } | null;
  setUploadedDocBack: (
    v: {
      url: string;
      path: string;
      encryption: EncryptionMeta;
      contentType: string;
    } | null,
  ) => void;
  uploadedEps: {
    url: string;
    path: string;
    encryption: EncryptionMeta;
    contentType: string;
  } | null;
  setUploadedEps: (
    v: {
      url: string;
      path: string;
      encryption: EncryptionMeta;
      contentType: string;
    } | null,
  ) => void;
}) {
  const {
    register,
    formState: { errors },
    setValue,
    watch,
    control,
  } = form;
  const { user } = useAuthStore();
  const docType = watch("idDocumentType");

  return (
    <div className="space-y-6">
      {/* Foto y documentos — obligatorios para la solicitud */}
      <div id="join-request-documents">
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Camera className="w-5 h-5 text-primary-600" />
              Foto y Documentos
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {(() => {
              const missing: string[] = [];
              if (!uploadedPhoto) missing.push("Foto tipo carnet");
              if (!uploadedDocFront) missing.push("Documento — Anverso");
              if (!uploadedDocBack) missing.push("Documento — Reverso");
              if (missing.length === 0) return null;
              return (
                <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 flex items-start gap-2 text-sm text-amber-800">
                  <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-semibold">
                      Faltan documentos obligatorios ({missing.length})
                    </p>
                    <ul className="list-disc list-inside text-amber-700 mt-0.5">
                      {missing.map((it) => (
                        <li key={it}>{it}</li>
                      ))}
                    </ul>
                    <p className="text-amber-700 mt-1">
                      Los documentos se guardarán de forma segura (encriptados)
                      y solo serán visibles para directores del club.
                    </p>
                  </div>
                </div>
              );
            })()}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <DocumentUpload
                currentUrl={uploadedPhoto?.url}
                title="Foto Tipo Carnet *"
                description="Obligatoria — fondo claro, rostro visible"
                icon={<User className="w-6 h-6 text-primary-500" />}
                onUpload={async (file) => {
                  if (!userId) throw new Error("Debes iniciar sesión");
                  const r = await uploadJoinRequestPhoto(file, userId);
                  setUploadedPhoto({ url: r.url, path: r.path });
                  return r;
                }}
                onRemove={() => setUploadedPhoto(null)}
              />
              <DocumentUpload
                currentUrl={uploadedDocFront?.url}
                encryption={uploadedDocFront?.encryption}
                contentType={uploadedDocFront?.contentType}
                title="Documento — Anverso *"
                description="Obligatorio — foto legible del frente (encriptada)"
                icon={<CreditCard className="w-6 h-6 text-gray-400" />}
                onUpload={async (file) => {
                  if (!userId) throw new Error("Debes iniciar sesión");
                  const r = await uploadJoinRequestDocumentFront(file, userId);
                  setUploadedDocFront({
                    url: r.url,
                    path: r.path,
                    encryption: r.encryption,
                    contentType: file.type,
                  });
                  return r;
                }}
                onRemove={() => setUploadedDocFront(null)}
              />
              <DocumentUpload
                currentUrl={uploadedDocBack?.url}
                encryption={uploadedDocBack?.encryption}
                contentType={uploadedDocBack?.contentType}
                title="Documento — Reverso *"
                description="Obligatorio — foto legible del dorso (encriptada)"
                icon={<CreditCard className="w-6 h-6 text-gray-400" />}
                onUpload={async (file) => {
                  if (!userId) throw new Error("Debes iniciar sesión");
                  const r = await uploadJoinRequestDocumentBack(file, userId);
                  setUploadedDocBack({
                    url: r.url,
                    path: r.path,
                    encryption: r.encryption,
                    contentType: file.type,
                  });
                  return r;
                }}
                onRemove={() => setUploadedDocBack(null)}
              />
              <DocumentUpload
                currentUrl={uploadedEps?.url}
                encryption={uploadedEps?.encryption}
                contentType={uploadedEps?.contentType}
                title="Soporte de EPS (opcional)"
                description="Carnet o certificado — encriptado"
                icon={<FileText className="w-6 h-6 text-gray-400" />}
                acceptPdf
                onUpload={async (file) => {
                  if (!userId) throw new Error("Debes iniciar sesión");
                  const r = await uploadJoinRequestEps(file, userId);
                  setUploadedEps({
                    url: r.url,
                    path: r.path,
                    encryption: r.encryption,
                    contentType: file.type,
                  });
                  return r;
                }}
                onRemove={() => setUploadedEps(null)}
              />
            </div>
            <p className="text-xs text-gray-500 pt-1">
              Los documentos se guardarán encriptados y solo serán visibles para
              los directores del club al aprobar tu solicitud.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Datos personales */}
      <Card className="border-0 shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5 text-primary-600" />
            Datos Personales
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Nombre *"
              {...register("firstName", {
                onBlur: (e: any) =>
                  setValue("firstName", toTitleCase(e.target.value), {
                    shouldValidate: true,
                  }),
              })}
              error={errors.firstName?.message}
              placeholder="Ej: Juan Carlos"
            />
            <Input
              label="Apellido *"
              {...register("lastName", {
                onBlur: (e: any) =>
                  setValue("lastName", toTitleCase(e.target.value), {
                    shouldValidate: true,
                  }),
              })}
              error={errors.lastName?.message}
              placeholder="Ej: Pérez González"
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tipo de Identificación *
              </label>
              <Select
                {...register("idDocumentType")}
                error={errors.idDocumentType?.message}
              >
                {Object.entries(documentTypeLabels).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            </div>
            <Input
              label="Número de Identificación *"
              {...register("idDocumentNumber", {
                onBlur: (e: any) =>
                  setValue(
                    "idDocumentNumber",
                    e.target.value.trim().toUpperCase(),
                    { shouldValidate: true },
                  ),
              })}
              onKeyDown={(e) => {
                if (["CC", "TI", "RC"].includes(docType)) filterDigits(e);
                else if (e.key === " ") e.preventDefault();
              }}
              onPaste={(e) => {
                if (["CC", "TI", "RC"].includes(docType)) {
                  const pasted = e.clipboardData.getData("text");
                  if (!/^\d+$/.test(pasted.trim())) {
                    e.preventDefault();
                  }
                }
              }}
              error={errors.idDocumentNumber?.message}
              placeholder="Ej: 1234567890"
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tipo de Sangre *
              </label>
              <Select
                {...register("bloodType")}
                error={errors.bloodType?.message}
              >
                <option value="">Seleccionar...</option>
                {bloodTypes.map((bt) => (
                  <option key={bt} value={bt}>
                    {bt}
                  </option>
                ))}
              </Select>
              <p className="text-xs text-gray-400 mt-1">
                Necesario para atención médica de emergencia
              </p>
            </div>
            <div>
              <DatePickerField
                control={control}
                name="birthDate"
                label="Fecha de Nacimiento *"
                max={new Date().toISOString().split("T")[0]}
                min="1900-01-01"
                error={errors.birthDate?.message}
              />
              {age !== null && (
                <p className="text-xs text-gray-500 mt-1">
                  Edad: {age} años
                  {derivedSection && (
                    <span>
                      {" "}
                      → Tipo de club:{" "}
                      {CLUB_TYPE_LABELS[derivedSection as ClubType]}
                    </span>
                  )}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Género *
              </label>
              <Select {...register("gender")} error={errors.gender?.message}>
                <option value="">Seleccionar...</option>
                <option value="male">Masculino</option>
                <option value="female">Femenino</option>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Teléfono *
              </label>
              <div className="flex gap-2">
                <select
                  {...register("phoneCountryCode")}
                  className="w-32 rounded-lg border-gray-300 text-sm"
                >
                  {COUNTRY_CODES.map((c) => (
                    <option key={`${c.code}-${c.name}`} value={c.code}>
                      {c.flag} {c.code} {c.name}
                    </option>
                  ))}
                </select>
                <Input
                  {...register("phone")}
                  onKeyDown={filterDigits}
                  onPaste={(e) => {
                    e.preventDefault();
                    const text = e.clipboardData
                      .getData("text")
                      .replace(/\D/g, "")
                      .slice(0, 10);
                    setValue("phone", text, { shouldValidate: true });
                  }}
                  error={errors.phone?.message}
                  placeholder="300 123 4567"
                  className="flex-1"
                />
              </div>
            </div>
            <div>
              <EpsSelect
                label="EPS / Sisbén (Opcional)"
                value={watch("healthInsurance") || ""}
                onChange={(v) =>
                  setValue("healthInsurance", v, { shouldDirty: true })
                }
                helperText="Puedes dejarlo en blanco si no la tienes a la mano"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <div className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                {user?.email}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Contacto de emergencia */}
      <Card className="border-0 shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Heart className="w-5 h-5 text-red-500" />
            Información de Contacto
          </CardTitle>
          <p className="text-sm text-gray-500 mt-1">
            Persona a quien contactar en caso de emergencia durante las
            actividades del club.
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Input
                label="Dirección *"
                {...register("address", {
                  onBlur: (e: any) =>
                    setValue("address", toTitleCase(e.target.value), {
                      shouldValidate: true,
                    }),
                })}
                error={errors.address?.message}
                placeholder="Ej: Carrera 10 # 20 - 30, Barrio Centro"
              />
            </div>
            <Input
              label="Persona de Contacto *"
              {...register("emergencyContactName", {
                onBlur: (e: any) =>
                  setValue(
                    "emergencyContactName",
                    toTitleCase(e.target.value),
                    { shouldValidate: true },
                  ),
              })}
              error={errors.emergencyContactName?.message}
              placeholder="Ej: María López"
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Relación con la Persona *
              </label>
              <Select
                {...register("emergencyContactRelationship")}
                error={errors.emergencyContactRelationship?.message}
              >
                <option value="">Seleccionar...</option>
                {relationships.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Teléfono de Contacto *
              </label>
              <div className="flex gap-2">
                <select
                  {...register("emergencyContactCountryCode")}
                  className="w-32 rounded-lg border-gray-300 text-sm"
                >
                  {COUNTRY_CODES.map((c) => (
                    <option key={`${c.code}-${c.name}`} value={c.code}>
                      {c.flag} {c.code} {c.name}
                    </option>
                  ))}
                </select>
                <Input
                  {...register("emergencyContactPhone")}
                  onKeyDown={filterDigits}
                  onPaste={(e) => {
                    e.preventDefault();
                    const text = e.clipboardData
                      .getData("text")
                      .replace(/\D/g, "")
                      .slice(0, 10);
                    setValue("emergencyContactPhone", text, {
                      shouldValidate: true,
                    });
                  }}
                  error={errors.emergencyContactPhone?.message}
                  placeholder="Ej: 3001234567"
                  className="flex-1"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ============================================
// STEP 3: CONFIRM
// ============================================

function StepConfirm({
  formValues,
  selectedClub,
  age,
  derivedSection,
  effectiveSection,
  sectionAdjusted,
  isCreatingClub,
  newClubData,
  requestedSection,
}: {
  formValues: PersonalForm;
  selectedClub: Club | null;
  age: number | null;
  derivedSection: string | null;
  effectiveSection: string | null;
  sectionAdjusted: boolean;
  isCreatingClub: boolean;
  newClubData: NewClubData | null;
  /** Sección que el aspirante declara dirigir (si aplica). */
  requestedSection: ClubType | null;
}) {
  return (
    <Card className="border-0 shadow-lg">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CheckCircle className="w-5 h-5 text-green-600" />
          Revisa tu solicitud
        </CardTitle>
        <p className="text-sm text-gray-500 mt-1">
          Verifica que toda la información sea correcta antes de enviarla.
        </p>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Club */}
          {isCreatingClub && newClubData ? (
            <div>
              <h4 className="text-sm font-medium text-gray-500 mb-2">
                Nuevo Club (Director)
              </h4>
              <div className="p-3 bg-emerald-50 rounded-lg">
                <p className="font-semibold text-emerald-800">
                  {newClubData.name}
                </p>
                <p className="text-sm text-emerald-600">
                  {[
                    newClubData.sections.aventureros && "Aventureros",
                    newClubData.sections.conquistadores && "Conquistadores",
                    newClubData.sections.guias_mayores && "Guías Mayores",
                  ]
                    .filter(Boolean)
                    .join(", ")}
                  {newClubData.churchName && ` • ${newClubData.churchName}`}
                </p>
                <p className="text-xs text-emerald-700 mt-1 font-medium">
                  Serás director de este club.
                </p>
              </div>
            </div>
          ) : (
            selectedClub && (
              <div>
                <h4 className="text-sm font-medium text-gray-500 mb-2">Club</h4>
                <div className="p-3 bg-primary-50 rounded-lg">
                  <p className="font-semibold text-primary-800">
                    {selectedClub.name}
                  </p>
                  <p className="text-sm text-primary-600">
                    {getActiveSections(selectedClub)
                      .map((s) => CLUB_TYPE_LABELS[s as ClubType])
                      .join(", ")}
                    {/* La "sección por edad" solo se muestra a quien entra como
                        miembro; a un director de sección lo confundiría con la
                        sección que va a dirigir. */}
                    {!requestedSection &&
                      effectiveSection &&
                      ` • Tu sección: ${CLUB_TYPE_LABELS[effectiveSection as ClubType]}`}
                  </p>
                  {requestedSection && (
                    <p className="text-xs text-primary-700 mt-1 font-medium">
                      Solicitas dirigir {CLUB_TYPE_LABELS[requestedSection]}. El
                      director general del club o la asociación revisará tu
                      solicitud.
                    </p>
                  )}
                </div>
                {!requestedSection &&
                  sectionAdjusted &&
                  derivedSection &&
                  effectiveSection && (
                    <div className="mt-2 flex items-start gap-2 p-2.5 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
                      <svg
                        className="w-3.5 h-3.5 mt-0.5 shrink-0 text-amber-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                        />
                      </svg>
                      <span>
                        Tu edad corresponde a{" "}
                        <strong>
                          {CLUB_TYPE_LABELS[derivedSection as ClubType]}
                        </strong>
                        , pero esa sección no está activa en este club. Serás
                        asignado a{" "}
                        <strong>
                          {CLUB_TYPE_LABELS[effectiveSection as ClubType]}
                        </strong>
                        .
                      </span>
                    </div>
                  )}
              </div>
            )
          )}

          {/* Datos personales */}
          <div>
            <h4 className="text-sm font-medium text-gray-500 mb-2">
              Datos Personales
            </h4>
            <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
              <div>
                <span className="text-gray-500">Nombre:</span>{" "}
                <span className="font-medium">
                  {formValues.firstName} {formValues.lastName}
                </span>
              </div>
              <div>
                <span className="text-gray-500">Documento:</span>{" "}
                <span className="font-medium">
                  {formValues.idDocumentType} {formValues.idDocumentNumber}
                </span>
              </div>
              <div>
                <span className="text-gray-500">Nacimiento:</span>{" "}
                <span className="font-medium">
                  {formValues.birthDate}
                  {age !== null && ` (${age} años)`}
                </span>
              </div>
              <div>
                <span className="text-gray-500">Género:</span>{" "}
                <span className="font-medium">
                  {formValues.gender === "male" ? "Masculino" : "Femenino"}
                </span>
              </div>
              <div>
                <span className="text-gray-500">Tipo de sangre:</span>{" "}
                <span className="font-medium">{formValues.bloodType}</span>
              </div>
              {/* Tipo de club por EDAD. A quien viene a dirigir no se le
                  muestra: se confundiría con el que solicita dirigir. */}
              {effectiveSection && !requestedSection && (
                <div>
                  <span className="text-gray-500">Tipo de club:</span>{" "}
                  <span className="font-medium">
                    {CLUB_TYPE_LABELS[effectiveSection as ClubType]}
                  </span>
                </div>
              )}
              <div>
                <span className="text-gray-500">Teléfono:</span>{" "}
                <span className="font-medium">
                  {formValues.phoneCountryCode} {formValues.phone}
                </span>
              </div>
              {formValues.email && (
                <div>
                  <span className="text-gray-500">Email:</span>{" "}
                  <span className="font-medium">{formValues.email}</span>
                </div>
              )}
              {formValues.healthInsurance && (
                <div>
                  <span className="text-gray-500">EPS/Sisbén:</span>{" "}
                  <span className="font-medium">
                    {formValues.healthInsurance}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Contacto */}
          <div>
            <h4 className="text-sm font-medium text-gray-500 mb-2">
              Contacto de Emergencia
            </h4>
            <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
              <div className="col-span-2">
                <span className="text-gray-500">Dirección:</span>{" "}
                <span className="font-medium">{formValues.address}</span>
              </div>
              <div>
                <span className="text-gray-500">Contacto:</span>{" "}
                <span className="font-medium">
                  {formValues.emergencyContactName}
                </span>
              </div>
              <div>
                <span className="text-gray-500">Relación:</span>{" "}
                <span className="font-medium">
                  {formValues.emergencyContactRelationship}
                </span>
              </div>
              <div>
                <span className="text-gray-500">Teléfono:</span>{" "}
                <span className="font-medium">
                  {formValues.emergencyContactCountryCode}{" "}
                  {formValues.emergencyContactPhone}
                </span>
              </div>
            </div>
          </div>

          <div
            className={`p-3 ${isCreatingClub ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"} border rounded-lg text-sm ${isCreatingClub ? "text-emerald-800" : "text-amber-800"}`}
          >
            <p>
              {isCreatingClub
                ? "Al confirmar, se creará tu club y quedarás registrado como director. Podrás invitar miembros desde el panel de administración."
                : "Al enviar, tu solicitud será revisada por el director del club. Recibirás acceso cuando sea aprobada."}
            </p>
            {!isCreatingClub && (
              <p className="mt-2 flex items-center gap-1.5 font-medium">
                <svg
                  className="w-4 h-4 shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                Tiempo de respuesta estimado: 24 a 48 horas
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
